import {
  Agreement,
  AgreementOutcome,
  AGREEXA_COMMISSION,
  AgreementStatus,
} from '../types'

const STORAGE_KEY = 'agreexa_agreements'
const OUTCOMES_KEY = 'agreexa_outcomes'
const PROVIDER_PROFILE_KEY = 'agreexa_provider_profile'

function getStoredAgreements(): Agreement[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    return data ? JSON.parse(data) : []
  } catch {
    return []
  }
}

function saveAgreements(agreements: Agreement[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(agreements))
}

function getStoredOutcomes(): AgreementOutcome[] {
  try {
    const data = localStorage.getItem(OUTCOMES_KEY)
    return data ? JSON.parse(data) : []
  } catch {
    return []
  }
}

function saveOutcomes(outcomes: AgreementOutcome[]) {
  localStorage.setItem(OUTCOMES_KEY, JSON.stringify(outcomes))
}

export function getProviderProfile(): import('../types').ProviderProfile {
  try {
    const data = localStorage.getItem(PROVIDER_PROFILE_KEY)
    if (data) return JSON.parse(data)
  } catch {}

  // Default profile for the prototype
  const defaultProfile = {
    id: 'prov_default',
    name: 'Prestador Demo',
    email: 'prestador@demo.com',
    mp_connected: false,
    mp_user_id: null,
    created_at: new Date().toISOString(),
  }
  localStorage.setItem(PROVIDER_PROFILE_KEY, JSON.stringify(defaultProfile))
  return defaultProfile
}

export function updateProviderProfile(
  patch: Partial<import('../types').ProviderProfile>,
): import('../types').ProviderProfile {
  const current = getProviderProfile()
  const updated = { ...current, ...patch }
  localStorage.setItem(PROVIDER_PROFILE_KEY, JSON.stringify(updated))
  return updated
}

export function generateAgreementCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let code = 'AGREE-'
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)]
  }
  return code
}

export async function createAgreement(data: {
  category: string
  outcome_type: string
  title: string
  description?: string
  provider_name?: string
  provider_email?: string
  client_email: string
  client_name?: string
  amount: number
  currency: string
  service_date?: string
  service_date_end?: string
  service_location?: string
}): Promise<Agreement> {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 800))

  const agreement_code = generateAgreementCode()
  const commission_rate = AGREEXA_COMMISSION
  const net_amount = parseFloat(
    (data.amount * (1 - commission_rate)).toFixed(2),
  )

  const newAgreement: Agreement = {
    id: crypto.randomUUID(),
    agreement_code,
    title: data.title,
    description: data.description || null,
    category: data.category as any,
    outcome_type: data.outcome_type as any,
    provider_name: data.provider_name || 'Prestador',
    provider_email: data.provider_email || null,
    provider_phone: null,
    client_email: data.client_email,
    client_name: data.client_name || null,
    amount: data.amount,
    currency: data.currency,
    commission_rate,
    net_amount,
    service_date: data.service_date || null,
    service_date_end: data.service_date_end || null,
    deadline: null,
    status: 'pending_payment',
    escrow_state: 'CREATED',
    mp_payment_id: null,
    created_at: new Date().toISOString(),
    paid_at: null,
    outcome_at: null,
    confirmed_at: null,
    completed_at: null,
  }

  const agreements = getStoredAgreements()
  agreements.push(newAgreement)
  saveAgreements(agreements)

  return newAgreement
}

export async function getAgreementByCode(
  code: string,
): Promise<Agreement | null> {
  await new Promise((resolve) => setTimeout(resolve, 400))
  const agreements = getStoredAgreements()
  return agreements.find((a) => a.agreement_code === code) || null
}

export async function getAllAgreements(): Promise<Agreement[]> {
  await new Promise((resolve) => setTimeout(resolve, 400))
  const agreements = getStoredAgreements()
  return agreements.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )
}

export async function updateAgreement(
  code: string,
  patch: Partial<Agreement>,
): Promise<Agreement> {
  await new Promise((resolve) => setTimeout(resolve, 500))
  const agreements = getStoredAgreements()
  const index = agreements.findIndex((a) => a.agreement_code === code)

  if (index === -1) throw new Error('Agreement not found')

  const updated = { ...agreements[index], ...patch }
  agreements[index] = updated
  saveAgreements(agreements)

  return updated
}

export async function registerOutcome(
  agreementId: string,
  outcomeData: Partial<AgreementOutcome>,
): Promise<AgreementOutcome> {
  await new Promise((resolve) => setTimeout(resolve, 600))

  const newOutcome: AgreementOutcome = {
    id: crypto.randomUUID(),
    agreement_id: agreementId,
    type: outcomeData.type as any,
    title: outcomeData.title || 'Resultado registrado',
    description: outcomeData.description || null,
    evidence_url: outcomeData.evidence_url || null,
    file_url: outcomeData.file_url || null,
    notes: outcomeData.notes || null,
    created_at: new Date().toISOString(),
  }

  const outcomes = getStoredOutcomes()
  outcomes.push(newOutcome)
  saveOutcomes(outcomes)

  return newOutcome
}

export async function getAgreementOutcomes(
  agreementId: string,
): Promise<AgreementOutcome[]> {
  await new Promise((resolve) => setTimeout(resolve, 300))
  const outcomes = getStoredOutcomes()
  return outcomes
    .filter((o) => o.agreement_id === agreementId)
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
}

export async function payAgreement(code: string): Promise<Agreement> {
  return updateAgreement(code, {
    status: 'active',
    escrow_state: 'FUNDED',
    paid_at: new Date().toISOString(),
  })
}
